<template>
  <div>
    <h1 class="page-header">{{ $t('productMovement.pageTitle') }}</h1>
    <ts-loading-banner :loading="loading">
      <ts-card>
        <ts-card-wrapper>
          <div class="ts-flex ts-justify-between">
            <div class="ts-space-x-3">
              <a-range-picker
                v-model="dateRange"
                :allowClear="false"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                style="width:200px"
                @change="fetchData()"
              />
              <ts-button outline @click.prevent="exportExcel()">{{
                $t('excel')
              }}</ts-button>
            </div>
            <div class="ts-flex ts-space-x-3">
              <ts-branch-filter @filter="value => (branch_id = value)" style="width:200px" />
              <a-select
                  show-search
                  v-model="product_id"
                  mode="multiple"
                  style="width: 250px"
                  :default-active-first-option="
                      false
                  "
                  :show-arrow="false"
                  :filter-option="false"
                  :not-found-content="null"
                  :maxTagCount="1"
                  @search="searchProduct"
              >
                  <a-select-option
                      v-for="c in productList"
                      :key="c.product_id"
                  >
                      {{ c.product_name_en }}
                  </a-select-option>
              </a-select>            
              <ts-button color="primary" @click.prevent="fetchData()">{{
                $t('search')
              }}</ts-button>
            </div>
          </div>
        </ts-card-wrapper>
        <div class="ts-overflow-scroll" style="height:600px">
          <ts-table :columns="columns" :records="resources">
            <template v-slot="{ record }">
              <td>
                <i
                  class="fas fa-arrow-right ts-text-green-600"
                  v-if="record.multiplier == 1"
                ></i>
                <i class="fas fa-arrow-left ts-text-red-600" v-else></i>
              </td>
              <td>{{ record.branch_name }}</td>
              <td>{{ record.transaction_date }}</td>
              <td>{{ record.location_name }}</td>
              <td>{{ record.barcode }}</td>
              <td>{{ record.product_name }}</td>
              <td
                class="ts-text-center ts-font-semibold"
                :class="
                  record.multiplier < 1 ? 'ts-text-red-600' : 'ts-text-green-600'
                "
              >
                {{ record.quantity * record.multiplier }}
              </td>
              <td>{{ record.trans_type }}</td>
              <td>{{ record.transaction_number }}</td>
            </template>
          </ts-table>
        </div>
        <div class="ts-flex ts-justify-end ts-p-4">
          <table border="1">
            <tr>
                <td colspan="3" class="ts-border ts-border-2 ts-text-center ts-px-2">{{ $t('productMovement.in') }}</td>
                <td colspan="4" class="ts-border ts-border-2 ts-text-center ts-px-2">{{ $t('productMovement.out') }}</td>
                <td rowspan="2" class="ts-border ts-border-2 ts-px-2">{{ $t('productMovement.ending') }}</td>
            </tr>
            <tr>
              <td class="ts-border ts-border-2 ts-px-2">{{ $t('productMovement.stockIn') }}</td>
              <td class="ts-border ts-border-2 ts-px-2">{{ $t('productMovement.transferIn') }}</td>
              <td class="ts-border ts-border-2 ts-px-2">{{ $t('productMovement.total') }}</td>
              <td class="ts-border ts-border-2 ts-px-2">{{ $t('productMovement.transferOut') }}</td>
              <td class="ts-border ts-border-2 ts-px-2">{{ $t('productMovement.retailSale') }}</td>
              <td class="ts-border ts-border-2 ts-px-2">{{ $t('productMovement.saleInvoice') }}</td>
              <td class="ts-border ts-border-2 ts-px-2">{{ $t('productMovement.total') }}</td>
            </tr>
            <tr>
              <td class="ts-text-center ts-border ts-border-2 ts-px-2">{{ sumTranType('Stock In') }}</td>
              <td class="ts-text-center ts-border ts-border-2 ts-px-2">{{ sumTranType('Transfer In') }}</td>
              <td class="ts-text-center ts-border ts-border-2 ts-px-2">{{ sumTranType('Stock In') + sumTranType('Transfer In') }}</td>
              <td class="ts-text-center ts-border ts-border-2 ts-px-2">{{ sumTranType('Transfer_out') }}</td>
              <td class="ts-text-center ts-border ts-border-2 ts-px-2">{{ sumTranType('Retail Sale') }}</td>
              <td class="ts-text-center ts-border ts-border-2 ts-px-2">{{ sumTranType('Sale Invoice') }}</td>
              <td class="ts-text-center ts-border ts-border-2 ts-px-2">{{ sumTranType('Sale Invoice') + sumTranType('Transfer_out') + sumTranType('Retail Sale')  }}</td>
              <td class="ts-text-center ts-border ts-border-2 ts-px-2">{{ sumEnding }}</td>
            </tr>
          </table>
        </div>
      </ts-card>
    </ts-loading-banner>
  </div>
</template>

<script>
import { debounce, sum, sumBy, trim } from 'lodash'
import { mapState } from 'vuex'
import * as FileDownload from 'downloadjs'

export default {
  name: 'productMovement',
  data() {
    return {
      loading: false,
      exporting: false,
      productList: [],
      columns: [
        { name: this.$t('productMovement.type') },
        { name: this.$t('productMovement.branch') },
        { name: this.$t('productMovement.date') },
        { name: this.$t('productMovement.location') },
        { name: this.$t('productMovement.barcode') },
        { name: this.$t('productMovement.productName') },
        {
          name: this.$t('productMovement.quantity'),
          style: 'text-align:center'
        },
        { name: this.$t('productMovement.actionFrom') },
        {
          name: this.$t('productMovement.transactionNumber'),
          style: 'width: 120px'
        }
      ]
    }
  },
  computed: {
    ...mapState('productMovement', ['resources', 'pagination']),
    product_id: {
      get() {
        return this.$store.state.productMovement.product_id
      },
      set(newValue) {
        this.$store.commit('productMovement/SET_PRODUCT_ID', newValue)
      }
    },
    branch_id: {
      get() {
        return this.$store.state.productMovement.branch_id
      },
      set(newValue) {
        this.$store.commit('productMovement/SET_BRANCH_ID', newValue)
      }
    },
    sumEnding: vm => {
      return sumBy(vm.resources, o => {
        return o.quantity * o.multiplier
      })
    },
    sumTranType: vm => type => {
      return sum(vm.resources.filter(el => el.trans_type == type).map(o => {
         return parseInt(o.quantity)
      }))
    },
    dateRange: {
      get() {
        return this.$store.state.productMovement.dateRange
      },
      set(newValue) {
        this.$store.commit('productMovement/SET_DATE_RANGE', newValue)
      }
    }
  },
  methods: {
    fetchData() {
      this.loading = true
      this.$store
        .dispatch('productMovement/fetch')
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    exportExcel() {
      this.exporting = true
      this.$store
        .dispatch('productMovement/excel')
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
        })
        .finally(() => (this.exporting = false))
    },
    searchProduct: debounce(function(value){
      this.loading = true
      this.$store
        .dispatch('productMovement/searchProduct',{
          search: value
        })
        .then(response => {
          this.productList = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    }, 800)
  },
  watch: {
    // search: debounce(function() {
    //   this.fetchData()
    // }, 500),
    branch_id: debounce(function() {
      this.fetchData()
    }, 500)
  }
}
</script>
